import React, { useEffect, useState } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { getVideoTranslationFile } from '../../../../api/course';
import {
  MediaViewer,
  VTTConfigItem,
} from '../../../../components/MediaViewer/MediaViewer';
import { SelectOptions } from '../../../../horizon-components/MMultSelect/MMultiSelect';
import { Unit, UnitItem } from '../../../../models/Course';
import { AiAssistant } from './AiAssistant';
import { TopicTest } from './TopicTest';

type Props = {
  currentUnit: Unit;
  currentUnitData: UnitItem;
  startExam: (unitId: number) => void;
  languages?: SelectOptions[];
  isStaff?: boolean;
  isPreview?: boolean;
};

export const CourseContentBody: React.FC<Props> = ({
  currentUnit,
  currentUnitData,
  startExam,
  languages,
  isStaff,
  isPreview,
}) => {
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  const [isTopicTestActive, setIsTopicTestActive] = useState(false);
  const [vttFiles, setVttFiles] = useState<VTTConfigItem[]>([]);
  const [vttDownloaded, setVttDownloaded] = useState(false);

  useEffect(() => {
    setCurrentTopicIndex(0);
  }, [currentUnit.id]);

  const totalTopics = currentUnitData.topics.length;
  const currentTopic = currentUnitData.topics[currentTopicIndex];
  const disablePrev = currentTopicIndex === 0 && !isTopicTestActive;
  const nextStartsTest = currentTopicIndex === totalTopics - 1;

  const topicMedia = currentTopic?.contents.find(
    (content) => content.resourcetype === 'FileContent'
  );
  const topicText = currentTopic?.contents.find(
    (content) => content.resourcetype === 'TextContent'
  );

  const onNextClick = () => {
    if (
      nextStartsTest &&
      !(
        !isTopicTestActive &&
        !!currentUnitData?.topics?.[currentTopicIndex]?.questions?.length
      )
    ) {
      startExam(currentUnit.id);
      return;
    }
    if (
      !isTopicTestActive &&
      !!currentUnitData?.topics?.[currentTopicIndex]?.questions?.length
    ) {
      setIsTopicTestActive(true);
      return;
    }

    setIsTopicTestActive(false);
    setCurrentTopicIndex(currentTopicIndex + 1);
  };

  const onPrevClick = () => {
    if (disablePrev) return;
    if (isTopicTestActive) {
      setIsTopicTestActive(false);
      return;
    }
    setCurrentTopicIndex(currentTopicIndex - 1);
  };

  const NavigationButtons = ({ showTitle = false }: { showTitle?: boolean }) => (
    <div className="flex items-center gap-3 sm-max:w-full">
      {/* <p className="text-base font-medium uppercase text-gray-600 dark:text-white">
        {totalTopics !== 1 && `Topic ${currentTopicIndex + 1}/${totalTopics}`}{' '}
        {isTopicTestActive && '(Test)'}
      </p> */}
      <div className="flex items-center justify-between gap-2 sm-max:w-full">
        <div
          onClick={onPrevClick}
          className={`linear flex h-[32px] w-[32px] items-center justify-center rounded-full border-[2px] border-primary-purple-blue-300 text-3xl text-primary-purple-blue-300 transition duration-200 hover:cursor-pointer hover:bg-primary-purple-blue-100 active:bg-primary-purple-blue-200 lg:h-[42px] lg:w-[42px] ${
            disablePrev && '!cursor-default opacity-50 hover:!bg-white'
          }`}
        >
          <MdChevronLeft />
        </div>
        {!!showTitle && (
          <div
            className={
              'overflow-hidden overflow-ellipsis font-bold text-secondary-dark-grey-900 md:hidden md:text-[20px]'
            }
          >
            {currentTopic?.title}
          </div>
        )}
        <div
          onClick={onNextClick}
          className={`linear flex h-[32px] w-[32px] items-center justify-center rounded-full bg-primary-purple-blue-500 text-3xl text-white transition duration-200 hover:cursor-pointer hover:bg-primary-purple-blue-600 active:bg-primary-purple-blue-700 lg:h-[42px] lg:w-[42px]`}
        >
          <MdChevronRight />
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (topicMedia?.id) {
      getVideoTranslationFile(topicMedia.id)
        .then((data) => {
          if (data?.results?.length) {
            const _vtt = data.results.map((item) => {
              const lang = languages?.find((el) => el.label === item.language);

              return {
                srcLang: lang?.label || '',
                src: item.file,
              };
            });
            setVttFiles(_vtt);
          }
        })
        .finally(() => {
          setVttDownloaded(true);
        });
    }
  }, [languages, topicMedia?.id]);

  return (
    <>
      {/* Header */}
      <div className="flex w-full items-center justify-between gap-3">
        <div
          className={
            'overflow-hidden overflow-ellipsis font-bold text-secondary-dark-grey-900 sm-max:hidden md:text-[20px]'
          }
        >
          {currentTopic?.title}
          {/* {currentUnitData.topics.length === 1 ? '' : currentTopic?.title} */}
        </div>
        <NavigationButtons showTitle />
      </div>
      {/*Content*/}
      {!isTopicTestActive && (
        <>
          {!!topicMedia?.file && (
            <MediaViewer
              link={topicMedia?.file || ''}
              vttFiles={vttFiles}
              vttDownloaded={vttDownloaded}
            />
          )}
          {topicText && topicText.text && (
            <div dangerouslySetInnerHTML={{ __html: topicText.text }} />
          )}

          {/*AI assistant*/}
          <AiAssistant topicId={currentTopic?.id} />
          <div className={'flex w-full justify-end'}>
            <NavigationButtons />
          </div>
        </>
      )}

      {/*Topic test*/}
      {isTopicTestActive && (
        <TopicTest
          topic={currentTopic}
          onNextClick={onNextClick}
          isStaff={isStaff}
          isPreview={isPreview}
        />
      )}
    </>
  );
};
